import { AUTH_SESSION_KEY, isTokenActive } from './auth-server';
import { clearItem, getItem, setItem } from './auth-session-cookie';

import { parseJwt } from '@src/utils/parse-jwt';

const clearUrlTokenFromWindow = (userWindow): void => {
	const url = new URL(userWindow.location.href);
	url.searchParams.delete('token');
	userWindow.history.replaceState({}, document.title, url.toString());
};

export function AuthClearSession(): void {
	clearItem(AUTH_SESSION_KEY);
}

export function AuthStoreSession(urlToken: string): void {
	const parsedJwtToken = parseJwt(urlToken);

	if (!parsedJwtToken?.exp) {
		console.error('Invalid token payload:', parsedJwtToken);
		clearItem(AUTH_SESSION_KEY);
		return;
	}

	const expiresAtMs = parsedJwtToken.exp * 1000;

	setItem(AUTH_SESSION_KEY, urlToken, expiresAtMs);
}

export function AuthGetSession(): {
	storedToken: string | null;
	storedTokenExpiry: string | null;
} {
	const { value, expiry } = getItem(AUTH_SESSION_KEY);

	if (!value || !expiry) {
		return {
			storedToken: null,
			storedTokenExpiry: null,
		};
	}

	return {
		storedToken: value,
		storedTokenExpiry: `${expiry}`,
	};
}

export function AuthFindTokenFromWindow(userWindow): string | null {
	const userWindowLocation = userWindow?.location;
	const foundToken = userWindowLocation && new URLSearchParams(userWindowLocation.search).get('token');
	return foundToken ?? '';
}

export function AuthValidateAndStoreToken(
	urlToken: string,
	userWindow
): {
	token: string;
	tokenExpired: boolean;
} {
	try {
		const currentToken = urlToken;
		clearUrlTokenFromWindow(userWindow);

		// Fresh URL tokens from login flows should win over any existing cookie session.
		if (currentToken) {
			AuthStoreSession(urlToken);

			const latestSession = AuthGetSession(); // to validate storage
			const isActive = isTokenActive(latestSession.storedTokenExpiry || '');
			return {
				token: latestSession.storedToken || '',
				tokenExpired: !isActive,
			};
		}

		// Existing Session fallback when no URL token is provided.
		const { storedToken, storedTokenExpiry } = AuthGetSession();

		if (storedToken && storedTokenExpiry) {
			// Validate stored token
			const isActive = isTokenActive(storedTokenExpiry);
			if (isActive) {
				return { token: storedToken, tokenExpired: false };
			}
		}

		// When all else fails - guarantee an expired response
		AuthClearSession();
		return { token: '', tokenExpired: true };
	} catch (error) {
		console.error('Error validating token:', error);
		return { token: '', tokenExpired: true };
	}
}
